import React, { useEffect, useState, useMemo, useRef } from "react";
import {
    Card,
    CardHeader,
    CardContent,
    CardActions,
    Avatar,
    Typography,
    IconButton,
    Box,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText,
    Paper,
    Popover,
    TextField,
    CircularProgress,
    Button,
} from "@mui/material";
import {
    Favorite,
    FavoriteBorder,
    ChatBubbleOutline,
    MoreHoriz,
    Info,
    Report,
    AccountCircle,
    Share,
    SendRounded,
    ThumbUpAltOutlined,
    ChatBubbleOutlineOutlined,
    ShareOutlined,
    DeleteForever as DeleteForeverIcon,
} from "@mui/icons-material";
import classes from "./Post.module.scss";
import { PostData, updatePostAsync, syncPostDetails } from "../../../store/slices/newsfeedSlice";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "../../../store/store";
import { fetchProfilePhoto, selectProfilePhoto } from "../../../store/slices/profileSlice";
import { RootState } from "../../../store/store";
import TimeAgo from "timeago-react";
import { PostCategory } from "../../../constants/categories";
import styles from "../../../pages/PostDetail/PostDetail.module.scss";
import EmojiEmotionsIcon from "@mui/icons-material/EmojiEmotions";
import data from "@emoji-mart/data";
import Picker from "@emoji-mart/react";
import { Link } from "react-router-dom";
import ReadMoreText from "../../ui/read-more-textbox/ReadMoreText";
import PostImages from "./PostImages";
import MediaGrid from "../../media/MediaGrid/MediaGrid";
import { getAbsoluteImageUrl } from "../../../utils/imageUtils";

// Helper function to safely parse timestamp string
const parseTimestamp = (timestamp: string | number | Date): Date | number => {
    try {
        const parsedDate = new Date(timestamp);

        // Check if the date is valid
        if (isNaN(parsedDate.getTime())) {
            console.warn(`[Post] Invalid timestamp received: ${timestamp}`);

            // Return current time as a fallback to avoid crashing TimeAgo
            return Date.now();
        }

        // Date is valid, return the parsed date
        return parsedDate;
    } catch (error) {
        console.error(`[Post] Error parsing timestamp: ${timestamp}`, error);

        // Return current time as a fallback
        return Date.now();
    }
};

interface CommentAuthor {
    authorId: string;
    name: string;
    profileImageUrl: string;
    profileUrl: string;
}

export interface CommentData {
    commentId: string;
    id?: string; // For key in map if commentId is not always present
    author: CommentAuthor;
    text: string;
    content?: string; // To support existing comment structures
    timestamp: string; // Ensuring this is string
    likesCount?: number; // Made optional
}

interface MediaItem {
    mediaType: "image" | "video" | "pdf" | "gif" | "document" | "file";
    mediaUrl: string;
    thumbnailUrl?: string;
    fileName?: string;
    fileSize?: number;
    duration?: string;
}

interface Author {
    authorId: string;
    name: string;
    profileUrl: string;
    profileImageUrl: string;
    jobTitle?: string;
}

interface PostProps {
    timestamp: string | number | Date;
    id: string;
    author: Author;
    content: {
        text: string;
    };
    media?: MediaItem[];
    postMetadata: {
        postDate: string;
        visibility: string;
        hashtags: string[];
        category: PostCategory;
    };
    engagement: {
        likesCount: number;
        commentsCount: number;
        shareCount: number;
    };
    comments: CommentData[];
    onShare?: (postId: string) => void;
    onViewDetails?: (postId: string) => void;
    onReport?: (postId: string) => void;
    onLike?: (post: PostData, liked: boolean) => void;
    onImageClick?: (index: number) => void;
    onComment?: (postId: string) => void; // To reload comments later
    onDelete?: (postId: string) => void;
}

const Post: React.FC<PostProps> = ({
    timestamp,
    id,
    author,
    content,
    media,
    postMetadata,
    comments,
    engagement,
    onShare,
    onViewDetails,
    onReport,
    onLike,
    onImageClick,
    onComment,
    onDelete,
}) => {
    const dispatch = useDispatch<AppDispatch>();

    const commentInputRef = useRef<HTMLInputElement>(null);

    const [liked, setLiked] = useState(false);
    const [currentLikesCount, setCurrentLikesCount] = useState(engagement?.likesCount ?? 0);

    const [showAllComments, setShowAllComments] = useState(false);
    const [newCommentText, setNewCommentText] = useState<string>("");
    const [commentError, setCommentError] = useState<boolean>(false);
    const [isSubmittingComment, setIsSubmittingComment] = useState<boolean>(false);

    const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
    const [emojiAnchorEl, setEmojiAnchorEl] = useState<null | HTMLElement>(null);
    const menuOpen = Boolean(menuAnchorEl);
    const emojiOpen = Boolean(emojiAnchorEl);

    const currentUserPhoto = useSelector(selectProfilePhoto);
    const { user, isAuthenticated } = useSelector((state: RootState) => state.auth);

    const [displayProfileImage, setDisplayProfileImage] = useState<string | null>(null);
    const [isCurrentUserPost, setIsCurrentUserPost] = useState<boolean>(false);

    const [internalComments, setInternalComments] = useState<CommentData[]>([]);

    const [isCarouselOpen, setIsCarouselOpen] = useState(false);
    const [carouselInitialIndex, setCarouselInitialIndex] = useState(0);

    // Process media URLs for all media types
    const processedMedia = useMemo(() => {
        if (!media) return [];
        return media.map((item) => {
            // Process different media types appropriately
            let processedUrl = item.mediaUrl;

            // For base64 strings, use as-is
            if (item.mediaUrl.startsWith("data:")) {
                processedUrl = item.mediaUrl;
            } else {
                // For URLs, process through getAbsoluteImageUrl for proper formatting
                processedUrl = getAbsoluteImageUrl(item.mediaUrl);
            }

            return {
                ...item,
                mediaUrl: processedUrl,
            };
        });
    }, [media]);

    // Legacy support: Keep processedImageUrls for existing PostImages component
    const processedImageUrls = useMemo(() => {
        if (!media) return [];
        return media
            .filter((item) => item.mediaType === "image")
            .map((item) => {
                // If it's already a base64 string, return as is
                if (item.mediaUrl.startsWith("data:image/")) {
                    return item.mediaUrl;
                }
                // Otherwise, process it through getAbsoluteImageUrl
                return getAbsoluteImageUrl(item.mediaUrl);
            });
    }, [media]);

    useEffect(() => {
        setIsCurrentUserPost(user?.id === author.authorId);
        const imageUrl =
            user?.id === author.authorId
                ? (currentUserPhoto ?? author.profileImageUrl ?? null)
                : (author.profileImageUrl ?? null);
        setDisplayProfileImage(imageUrl);

        if (user?.id === author.authorId && !currentUserPhoto && user?.id) {
            dispatch(fetchProfilePhoto(user.id));
        }
    }, [user, author, currentUserPhoto, dispatch]);

    useEffect(() => {
        if (Array.isArray(comments)) {
            const conformingComments: CommentData[] = comments.map((comment) => ({
                ...comment,
                author: {
                    ...comment.author,
                    profileImageUrl: comment.author?.profileImageUrl || "",
                    profileUrl: comment.author?.profileUrl || "",
                },
                text: comment.text || comment.content || "",
                timestamp:
                    typeof comment.timestamp === "string"
                        ? comment.timestamp
                        : new Date(comment.timestamp).toISOString(),
            }));
            setInternalComments(conformingComments);
        } else {
            setInternalComments([]);
        }
    }, [comments]);

    const handleLike = async (event: React.MouseEvent) => {
        event.stopPropagation();
        if (!isAuthenticated || !user) {
            // console.log("[Post] User not authenticated. Cannot like post.");
            return;
        }

        const newLikedState = !liked;
        setLiked(newLikedState);
        setCurrentLikesCount((prevLikes) => (newLikedState ? prevLikes + 1 : prevLikes - 1));

        if (onLike) {
            const postForLike: Omit<PostData, "userLikeStatus"> & {
                engagement: PostData["engagement"];
                comments: CommentData[];
            } = {
                id,
                author,
                content,
                postMetadata,
                engagement: {
                    likesCount: engagement.likesCount,
                    commentsCount: engagement.commentsCount,
                    shareCount: engagement.shareCount,
                },
                comments: internalComments,
                timestamp: postMetadata.postDate,
            };
            onLike(postForLike as PostData, newLikedState);
        }
    };

    const handleImageClick = (index: number) => {
        setCarouselInitialIndex(index);
        setIsCarouselOpen(true);
    };

    const handleCommentClick = (event: React.MouseEvent) => {
        event.stopPropagation();
        if (onViewDetails) {
            onViewDetails(id);
        }
    };

    const handleShareClick = (event: React.MouseEvent) => {
        event.stopPropagation();
        if (onShare) {
            onShare(id);
        }
    };

    const handleReplyClick = (commentAuthorName: string) => {
        if (commentInputRef.current) {
            setNewCommentText((prev) => `@${commentAuthorName} ${prev}`);
            commentInputRef.current.focus();
        }
    };

    const handleAddComment = async () => {
        if (!newCommentText.trim()) {
            setCommentError(true);
            return;
        }
        setCommentError(false);
        setIsSubmittingComment(true);

        if (!isAuthenticated || !user) {
            console.error("User not authenticated to comment.");
            setIsSubmittingComment(false);
            return;
        }

        const tempCommentId = `temp-${Date.now()}`;
        const optimisticComment: CommentData = {
            commentId: tempCommentId,
            author: {
                authorId: user.id,
                name:
                    user.firstName && user.lastName
                        ? `${user.firstName} ${user.lastName}`
                        : user.firstName || "MAX User",
                profileImageUrl: currentUserPhoto || "",
                profileUrl: currentUserPhoto || "",
            },
            text: newCommentText,
            timestamp: new Date().toISOString(),
            likesCount: 0,
        };

        setInternalComments((prevComments) => [...prevComments, optimisticComment]);
        setNewCommentText("");

        try {
            const updatePayload: Partial<PostData> & { id: string } = {
                id: id,
                comments: [...internalComments, optimisticComment],
            };
            await dispatch(updatePostAsync(updatePayload)).unwrap();
        } catch (error) {
            console.error("Failed to add comment:", error);
            setInternalComments((prevComments) =>
                prevComments.filter((c) => c.commentId !== tempCommentId),
            );
            setCommentError(true);
        } finally {
            setIsSubmittingComment(false);
        }
    };

    const handleEmojiClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        setEmojiAnchorEl(event.currentTarget);
    };

    const handleEmojiSelect = (emoji: { native: string }) => {
        setNewCommentText((prevText) => prevText + emoji.native);
        setEmojiAnchorEl(null);
    };

    const handleCloseEmoji = () => {
        setEmojiAnchorEl(null);
    };

    const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
        event.stopPropagation();
        setMenuAnchorEl(event.currentTarget);
    };

    const handleCloseMenu = (event?: React.MouseEvent<HTMLElement> | object, reason?: string) => {
        if (reason === "backdropClick" || reason === "escapeKeyDown") {
            setMenuAnchorEl(null);
        } else if (event && "stopPropagation" in event) {
            (event as React.MouseEvent<HTMLElement>).stopPropagation();
            setMenuAnchorEl(null);
        } else {
            setMenuAnchorEl(null);
        }
    };

    const handleSharePost = (event: React.MouseEvent<HTMLElement>) => {
        event.stopPropagation();
        if (onShare) onShare(id);
        handleCloseMenu();
    };

    const handleViewDetails = (event: React.MouseEvent<HTMLElement>) => {
        event.stopPropagation();
        if (onViewDetails) onViewDetails(id);
        handleCloseMenu();
    };

    const handleReportPost = (event: React.MouseEvent<HTMLElement>) => {
        event.stopPropagation();
        if (onReport) {
            onReport(id);
        }
        handleCloseMenu();
    };

    const handleDeletePost = (event: React.MouseEvent<HTMLElement>) => {
        event.stopPropagation();
        if (onDelete) {
            onDelete(id);
        }
        handleCloseMenu();
    };

    const handlePostClick = (event: React.MouseEvent) => {
        if (onViewDetails) {
            onViewDetails(id);
        }
    };

    const authorName = author?.name || "MAX Member";
    const authorTitle = author?.jobTitle || "MAX Aspire Member";

    const postImageUrl =
        media &&
        media.length > 0 &&
        media[0].mediaUrl &&
        (media[0].mediaUrl.startsWith("http://") || media[0].mediaUrl.startsWith("https://"))
            ? media[0].mediaUrl
            : null;
    const postVideoUrl =
        media && media.length > 0 && media[0].mediaUrl && media[0].mediaUrl.endsWith(".mp4")
            ? media[0].mediaUrl
            : null;

    const visibleComments = showAllComments ? internalComments : internalComments.slice(0, 2);

    return (
        <Card className={classes.postCard}>
            <Box className={classes.postHeader} onClick={handlePostClick}>
                <Box className={classes.avatarContainer}>
                    <Avatar
                        src={getAbsoluteImageUrl(displayProfileImage || "") || undefined}
                        alt={authorName}
                        className={classes.avatar}
                    >
                        {!displayProfileImage && <AccountCircle />}
                    </Avatar>
                </Box>
                <Box className={classes.userInfo}>
                    <Typography variant="subtitle1" className={classes.userName}>
                        {authorName}
                    </Typography>
                    <Typography variant="body2" className={classes.userTitle}>
                        {authorTitle}
                    </Typography>
                    <Typography variant="caption" className={classes.postTimestamp}>
                        <TimeAgo datetime={parseTimestamp(timestamp || postMetadata?.postDate)} />
                    </Typography>
                </Box>
                <IconButton
                    aria-label="settings"
                    onClick={handleOpenMenu}
                    sx={{ marginLeft: "auto" }}
                >
                    <MoreHoriz />
                </IconButton>
            </Box>

            <CardContent className={classes.postBody} onClick={handlePostClick}>
                <ReadMoreText text={content.text} maxLines={3} />

                {processedMedia.length > 0 && (
                    <Box
                        className={classes.postImageContainer}
                        onClick={(event) => {
                            // Prevent clicks on media container from triggering post navigation
                            event.stopPropagation();
                        }}
                    >
                        <MediaGrid
                            media={processedMedia}
                            maxDisplayItems={4}
                            onMediaClick={(media, index) => {
                                // Handle media click - for images/videos open in modal, for documents/PDFs download
                                if (["image", "gif"].includes(media.mediaType.toLowerCase())) {
                                    handleImageClick(index);
                                }
                                // For other media types, MediaGrid handles the click internally
                                // Note: Event propagation is now handled within MediaGrid itself
                            }}
                        />
                    </Box>
                )}
            </CardContent>

            <CardActions disableSpacing className={classes.cardActions}>
                <Box className={classes.actionButtonContainer}>
                    {/* TODO: Add like button back in */}
                    {/* <button
                        className={classes.actionButton}
                        onClick={handleLike}
                        aria-label={liked ? "Unlike post" : "Like post"}
                    >
                        <span className={classes.icon}>
                            {liked ? <Favorite sx={{ color: "red" }} /> : <FavoriteBorder />}
                        </span>
                        <span className={classes.count}>{currentLikesCount}</span>
                    </button> */}
                    <button
                        className={classes.actionButton}
                        onClick={handleCommentClick}
                        aria-label="View comments"
                    >
                        <span className={classes.icon}>
                            <ChatBubbleOutline />
                        </span>
                        <span className={classes.count}>{engagement?.commentsCount ?? 0}</span>
                    </button>
                    <button
                        className={classes.actionButton}
                        onClick={handleShareClick}
                        aria-label="Share post"
                    >
                        <span className={classes.icon}>
                            <ShareOutlined />
                        </span>
                        <span className={classes.count}>Share</span>
                    </button>
                </Box>
            </CardActions>

            <Box className={classes.commentsSection}>
                {isAuthenticated && (
                    <Box className={classes.commentInputArea}>
                        <Avatar
                            src={getAbsoluteImageUrl(currentUserPhoto || "") || undefined}
                            className={classes.commentInputAvatar}
                        >
                            {!currentUserPhoto && <AccountCircle />}
                        </Avatar>
                        <Box className={classes.commentInputFieldContainer}>
                            <TextField
                                fullWidth
                                multiline
                                variant="standard"
                                placeholder="Write a comment..."
                                value={newCommentText}
                                inputRef={commentInputRef}
                                onChange={(e) => setNewCommentText(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter" && !e.shiftKey) {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        handleAddComment();
                                    }
                                }}
                                error={commentError}
                                helperText={commentError ? "Comment cannot be empty." : ""}
                                InputProps={{
                                    disableUnderline: true,
                                    className: classes.commentInputField,
                                }}
                                sx={{ padding: 0 }}
                            />
                            <Box className={classes.commentActionButtonsContainer}>
                                <IconButton
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleAddComment();
                                    }}
                                    disabled={isSubmittingComment}
                                    className={classes.commentSendButton}
                                    size="small"
                                >
                                    {isSubmittingComment ? (
                                        <CircularProgress size={20} />
                                    ) : (
                                        <SendRounded />
                                    )}
                                </IconButton>
                                <IconButton
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleEmojiClick(e);
                                    }}
                                    className={classes.commentEmojiButton}
                                    size="small"
                                >
                                    <EmojiEmotionsIcon />
                                </IconButton>
                            </Box>
                        </Box>
                    </Box>
                )}
                <Popover
                    open={emojiOpen}
                    anchorEl={emojiAnchorEl}
                    onClose={handleCloseEmoji}
                    anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                    transformOrigin={{ vertical: "top", horizontal: "right" }}
                >
                    <Picker data={data} onEmojiSelect={handleEmojiSelect} />
                </Popover>

                {visibleComments.map((comment: CommentData, index: number) => (
                    <Box
                        key={comment.id || comment.commentId || index}
                        className={classes.commentItem}
                    >
                        <Box className={classes.commentAvatarContainer}>
                            <Link
                                to={`/profile/${comment.author?.authorId}`}
                                className={classes.undecoratedLink}
                            >
                                <Avatar
                                    src={
                                        getAbsoluteImageUrl(
                                            comment.author?.profileImageUrl || "",
                                        ) || undefined
                                    }
                                    alt={comment.author?.name || "User"}
                                    className={classes.commentAvatar}
                                >
                                    {!comment.author?.profileImageUrl && <AccountCircle />}
                                </Avatar>
                            </Link>
                        </Box>
                        <Box className={classes.commentContent}>
                            <Box className={classes.commentBubble}>
                                <Link
                                    to={`/profile/${comment.author?.authorId}`}
                                    className={classes.undecoratedLink}
                                >
                                    <Typography
                                        variant="subtitle2"
                                        className={classes.commentAuthorName}
                                    >
                                        {comment.author?.name || "Anonymous"}
                                    </Typography>
                                </Link>
                                <Typography
                                    variant="body2"
                                    className={classes.commentText}
                                    sx={{ whiteSpace: "pre-line" }}
                                >
                                    {comment.text || comment.content}
                                </Typography>
                            </Box>
                            <Box className={classes.commentActions}>
                                {/* <button className={classes.commentActionButton}>Like</button> */}
                                <button
                                    className={classes.commentActionButton}
                                    onClick={() => handleReplyClick(comment.author?.name || "User")}
                                >
                                    Reply
                                </button>
                                <Typography variant="caption" className={classes.commentTimestamp}>
                                    {comment.timestamp && (
                                        <TimeAgo datetime={parseTimestamp(comment.timestamp)} />
                                    )}
                                </Typography>
                            </Box>
                        </Box>
                    </Box>
                ))}

                {internalComments.length > 2 && !showAllComments && (
                    <Button
                        onClick={() => setShowAllComments(true)}
                        size="small"
                        sx={{
                            color: "var(--view-all-comments-button-color)",
                            textTransform: "none",
                            padding: "0.25rem 0",
                        }}
                    >
                        View all {internalComments.length} comments
                    </Button>
                )}
                {internalComments.length > 0 && showAllComments && (
                    <Button
                        onClick={() => setShowAllComments(false)}
                        size="small"
                        sx={{
                            color: "var(--view-all-comments-button-color)",
                            textTransform: "none",
                            padding: "0.25rem 0",
                        }}
                    >
                        Show fewer comments
                    </Button>
                )}
            </Box>

            <Menu
                id="post-options-menu"
                anchorEl={menuAnchorEl}
                open={menuOpen}
                onClose={handleCloseMenu}
                MenuListProps={{
                    "aria-labelledby": "post-options-button",
                }}
                PaperProps={{
                    className: classes.optionsMenu,
                }}
            >
                {isCurrentUserPost && (
                    <MenuItem onClick={handleDeletePost} className={classes.deleteMenuItem}>
                        <ListItemIcon>
                            <DeleteForeverIcon fontSize="small" className={classes.deleteIcon} />
                        </ListItemIcon>
                        <ListItemText>Delete Post</ListItemText>
                    </MenuItem>
                )}
                <MenuItem onClick={handleViewDetails}>
                    <ListItemIcon>
                        <Info fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>View Details</ListItemText>
                </MenuItem>
                <MenuItem onClick={handleSharePost}>
                    <ListItemIcon>
                        <Share fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Share Post</ListItemText>
                </MenuItem>
                {!isCurrentUserPost && (
                    <MenuItem onClick={handleReportPost}>
                        <ListItemIcon>
                            <Report fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Report Post</ListItemText>
                    </MenuItem>
                )}
            </Menu>
        </Card>
    );
};

export default Post;
